import sortBy from 'lodash/sortBy';

import {
  DragItem,
  DropZone,
} from '../../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import {
  DragItemSmall,
  DropItem as DropItemSmall,
} from './interactive-response-categorise-small.constants';
import { shuffleArray } from '../../../shared/interactive-drag-drop/interactive-drag-drop.utils';
import {
  InteractiveResponseCategoriesCategory,
  InteractiveResponseCategoriseItem,
} from '../interactive-response-categorise.constants';
import { InteractiveResponseAnswerFragment } from 'approot/activity-builder/present/__generated__/InteractiveResponseAnswerFragment';
import { InquisitiveActivitiesResponseVariant } from 'global/constants';

export function initialiseDragItems({
  items,
  shouldShuffle,
  variant,
}: {
  items: InteractiveResponseCategoriseItem[];
  shouldShuffle: boolean;
  variant: InquisitiveActivitiesResponseVariant;
}): DragItem[] {
  const sortedItems = sortBy(items, 'sort');
  const shuffled = shouldShuffle ? shuffleArray([...sortedItems]) : sortedItems;

  const dragItems = shuffled.map(item => ({
    id: item.itemId,
    value: item.categoryId || '',
    isDropped: false,
    question: {
      label: item.questionLabel || '1',
      text: item.text,
      richtext: item.richtext,
      audioUrl: item.audio?.original || null,
      baseColour: item.baseColour,
      variant,
      image: {
        itemId: item.itemId,
        small: item?.imageUrls?.[0]?.urls?.[0]?.url || null,
        large: item?.imageUrls?.[1]?.urls?.[0]?.url || null,
        description: item.image?.description || '',

        // existing content in prod will have imageMode = null
        isFit: item.imageMode === null ? false : !item.cropData,
      },
    },
  }));
  return dragItems;
}

type Answers = {
  id: string;
  isCorrect: boolean;
  label: string;
  zones: DropZone[];
};

export function getAnswers(
  attemptedAnswers: InteractiveResponseAnswerFragment,
  items: InteractiveResponseCategoriseItem[],
  categories: InteractiveResponseCategoriesCategory[],
  isFreeResponse?: boolean
) {
  const sortedItems = sortBy(items, 'sort');
  const answers: Answers[] = categories.map(category => ({
    id: category.id,
    isCorrect: false,
    label: category.label,
    zones: [],
  }));

  for (let i = 0; i < attemptedAnswers.interactiveResponseItems.length; i++) {
    const attemptedItem = attemptedAnswers.interactiveResponseItems[i];
    const item = sortedItems.find(
      item => item.itemId === attemptedItem.imageId
    );
    const answerZone = answers.findIndex(
      answer => answer.id === attemptedItem.dropZoneId
    );
    const isCorrect = isFreeResponse
      ? undefined
      : attemptedItem.isCorrect || false;

    answers[answerZone].zones.push({
      dragItemId: item?.itemId || '',
      previewItemId: item?.imageUrls?.[0]?.urls?.[0]?.url || null,
      isCorrect,
    });
  }

  return answers;
}

type DropItem = {
  id: string;
  label: string;
  zones: DropZone[];
};

export function populateAnswers({
  attemptedAnswers,
  items,
  categories,
  isFreeResponse,
  variant,
}: {
  attemptedAnswers: InteractiveResponseAnswerFragment;
  items: InteractiveResponseCategoriseItem[];
  categories: InteractiveResponseCategoriesCategory[];
  isFreeResponse?: boolean;
  variant: InquisitiveActivitiesResponseVariant;
}) {
  const dropItems: DropItem[] = [
    ...categories.map(category => ({
      id: category.id,
      label: category.label,
      zones: [],
    })),
  ];

  for (let i = 0; i < attemptedAnswers.interactiveResponseItems.length; i++) {
    const attemptedItem = attemptedAnswers.interactiveResponseItems[i];
    const item = items.find(item => item.itemId === attemptedItem.imageId);
    const dropIndex = dropItems.findIndex(
      dropItem => dropItem.id === attemptedItem.dropZoneId
    );
    const isCorrect = isFreeResponse
      ? undefined
      : attemptedItem.isCorrect || false;

    dropItems[dropIndex].zones.push({
      dragItemId: item?.itemId || '',
      previewItemId: item?.imageUrls?.[0]?.urls?.[0]?.url || null,
      isCorrect,
    });
  }

  const dragItems = initialiseDragItems({
    items,
    shouldShuffle: false,
    variant,
  });

  dragItems.map(dragItem => {
    dragItem.isDropped = true;
    return dragItem;
  });

  return {
    dragItems,
    dropItems,
  };
}

export function populateAnswersSmall({
  attemptedAnswers,
  items,
  categories,
  isFreeResponse,
  variant,
}: {
  attemptedAnswers: InteractiveResponseAnswerFragment;
  items: InteractiveResponseCategoriseItem[];
  categories: InteractiveResponseCategoriesCategory[];
  isFreeResponse?: boolean;
  variant: InquisitiveActivitiesResponseVariant;
}) {
  const dropItems: DropItemSmall[] = [
    ...categories.map(category => ({
      id: category.id,
      label: category.label,
      dragItems: {},
    })),
  ];
  for (let i = 0; i < attemptedAnswers.interactiveResponseItems.length; i++) {
    const attemptedItem = attemptedAnswers.interactiveResponseItems[i];
    const item = items.find(item => item.itemId === attemptedItem.imageId);
    const dropIndex = dropItems.findIndex(
      dropItem => dropItem.id === attemptedItem.dropZoneId
    );
    const isCorrect = isFreeResponse
      ? undefined
      : attemptedItem.isCorrect || false;

    dropItems[dropIndex].dragItems[item?.itemId || ''] = {
      isCorrect,
      isAnswered: true,
    };
  }

  const dragItems: DragItemSmall[] = initialiseDragItems({
    items,
    shouldShuffle: false,
    variant,
  });

  dragItems.map(dragItem => {
    dragItem.isDropped = true;
    dragItem.isCorrect = dropItems.some(
      dropItem => dropItem.dragItems[dragItem.id]?.isCorrect
    );
    return dragItem;
  });

  return {
    dragItems,
    dropItems,
  };
}

export function initialise({
  items,
  categories,
  isFreeResponse,
  isShowNumberOfItemsHint,
  shouldShuffle = true,
  variant,
}: {
  items: InteractiveResponseCategoriseItem[];
  categories: InteractiveResponseCategoriesCategory[];
  isFreeResponse: boolean;
  isShowNumberOfItemsHint: boolean;
  shouldShuffle: boolean;
  variant: InquisitiveActivitiesResponseVariant;
}) {
  const dropItems = categories.map(category => {
    const zones: DropZone[] = [];
    items.forEach(item => {
      // for free responses and when hints are hidden add equal dropzones on each side since
      // its mayhem and there are no real answers
      if (
        isFreeResponse ||
        !isShowNumberOfItemsHint ||
        item.categoryId === category.id
      ) {
        zones.push({
          dragItemId: null,
          previewItemId: null,
          isCorrect: undefined,
        });
      }
    });
    return {
      id: category.id,
      label: category.label,
      zones,
    };
  });

  return {
    dragItems: initialiseDragItems({ items, shouldShuffle, variant }),
    dropItems,
  };
}
